/**
 * Inline SVG icon set — stroke-based, inherits currentColor.
 * Keeps the web app free of bundled icon assets.
 */
import type { SVGProps } from 'react';

export type IconProps = SVGProps<SVGSVGElement> & { size?: number };

const base = (size: number): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
});

const filled = (size: number): SVGProps<SVGSVGElement> => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'currentColor',
  'aria-hidden': true,
});

export const HomeIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 21" {...rest}>
    <path d="M10 0C10.6202 0 11.2229 0.192339 11.7266 0.546875L11.9365 0.708984L11.9414 0.712891L18.9365 6.70801L19.0586 6.81641C19.3356 7.07771 19.561 7.38997 19.7217 7.73633C19.9052 8.13193 20 8.56292 20 8.99902V17.999C20 18.7947 19.6837 19.5585 19.1211 20.1211C18.6289 20.6131 17.9834 20.9161 17.2969 20.9844L17 20.999H14V13C14 11.8954 13.1046 11 12 11H8C6.89543 11 6 11.8954 6 13V20.999H3C2.20447 20.999 1.44149 20.6835 0.878906 20.1211C0.316297 19.5585 0 18.7947 0 17.999V8.99902C0 8.56292 0.0947873 8.13193 0.27832 7.73633C0.461973 7.34053 0.730245 6.98968 1.06348 6.70801L8.05859 0.712891L8.06348 0.708984C8.60496 0.251348 9.29104 0 10 0Z" />
  </svg>
);

export const BuddiesIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 20 20" {...rest}>
    <circle cx="7.5" cy="7.5" r="2.5" strokeWidth="1.7" />
    <path d="M3.1 15.4c0-2.7 2-4.6 4.4-4.6s4.4 1.9 4.4 4.6" strokeWidth="1.7" />
    <path d="M13.9 9.9 13.3 9.4C12 8.1 11.1 7.3 11.1 6.2c0-.8.7-1.5 1.5-1.5.5 0 .9.2 1.3.6.3-.4.8-.6 1.2-.6.9 0 1.6.7 1.6 1.5 0 1.1-.9 1.9-2.2 3.2l-.6.5Z" strokeWidth="1.4" />
  </svg>
);

export const CreateIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 16 16" {...rest}>
    <path d="M3.33333 8H12.6667" strokeWidth="1.33333" />
    <path d="M8 3.33301V12.6663" strokeWidth="1.33333" />
  </svg>
);

export const BellIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 20 17" {...rest}>
    <path d="M1.26127 14.326C1.13063 14.4692 1.04442 14.6472 1.01313 14.8385C0.981827 15.0298 1.00679 15.226 1.08498 15.4034C1.16316 15.5807 1.29121 15.7316 1.45353 15.8375C1.61585 15.9434 1.80545 15.9999 1.99927 16H17.9993C18.1931 16.0001 18.3827 15.9438 18.5451 15.8381C18.7076 15.7324 18.8358 15.5817 18.9142 15.4045C18.9926 15.2273 19.0178 15.0311 18.9867 14.8398C18.9557 14.6485 18.8697 14.4703 18.7393 14.327C17.4093 12.956 15.9993 11.499 15.9993 7C15.9993 5.4087 15.3671 3.88258 14.2419 2.75736C13.1167 1.63214 11.5906 1 9.99927 1C8.40797 1 6.88185 1.63214 5.75663 2.75736C4.63141 3.88258 3.99927 5.4087 3.99927 7C3.99927 11.499 2.58827 12.956 1.26127 14.326Z" />
    <path d="M8.2 14.3c.35.62 1.05 1 1.8 1 .75 0 1.45-.38 1.82-1" />
  </svg>
);

export const RobiIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 4.5v2.1" />
    <rect x="6" y="7" width="12" height="9" rx="3" />
    <circle cx="9.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="14.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
    <path d="M9 14h6" />
  </svg>
);

export const MessagesIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 23 22.1" {...rest}>
    <path d="M11.5 18C17.299 18 22 14.1944 22 9.5C22 4.80558 17.299 1 11.5 1C5.70101 1 1 4.80558 1 9.5C1 11.816 2.14422 13.9157 4 15.4488L3.5 20L7.31727 17.2988C8.59888 17.7499 10.0134 18 11.5 18Z" strokeWidth="2" />
    <circle cx="8.5" cy="9.5" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="11.5" cy="9.5" r="1.3" fill="currentColor" stroke="none" />
    <circle cx="14.5" cy="9.5" r="1.3" fill="currentColor" stroke="none" />
  </svg>
);

export const UserIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20.5c0-4 3.4-7 7.5-7s7.5 3 7.5 7" />
  </svg>
);

export const FilterIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 16 16" {...rest}>
    <path d="M14.6663 2H1.33301L6.66634 8.30667V12.6667L9.33301 14V8.30667L14.6663 2Z" strokeWidth="1.33333" />
  </svg>
);

export const MoreIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M5 12h14M5 6h14M5 18h14" />
  </svg>
);

export const PostMenuIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M7 12C7 12.5304 6.7893 13.0391 6.4142 13.4142C6.0391 13.7893 5.5304 14 5 14C4.4696 14 3.9609 13.7893 3.5858 13.4142C3.2107 13.0391 3 12.5304 3 12C3 11.4696 3.2107 10.9609 3.5858 10.5858C3.9609 10.2107 4.4696 10 5 10C5.5304 10 6.0391 10.2107 6.4142 10.5858C6.7893 10.9609 7 11.4696 7 12ZM14 12C14 12.5304 13.7893 13.0391 13.4142 13.4142C13.0391 13.7893 12.5304 14 12 14C11.4696 14 10.9609 13.7893 10.5858 13.4142C10.2107 13.0391 10 12.5304 10 12C10 11.4696 10.2107 10.9609 10.5858 10.5858C10.9609 10.2107 11.4696 10 12 10C12.5304 10 13.0391 10.2107 13.4142 10.5858C13.7893 10.9609 14 11.4696 14 12ZM21 12C21 12.5304 20.7893 13.0391 20.4142 13.4142C20.0391 13.7893 19.5304 14 19 14C18.4696 14 17.9609 13.7893 17.5858 13.4142C17.2107 13.0391 17 12.5304 17 12C17 11.4696 17.2107 10.9609 17.5858 10.5858C17.9609 10.2107 18.4696 10 19 10C19.5304 10 20.0391 10.2107 20.4142 10.5858C20.7893 10.9609 21 11.4696 21 12Z" />
  </svg>
);

export const VerticalKebabIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M12 7C12.5304 7 13.0391 6.7893 13.4142 6.4142C13.7893 6.0391 14 5.5304 14 5C14 4.4696 13.7893 3.9609 13.4142 3.5858C13.0391 3.2107 12.5304 3 12 3C11.4696 3 10.9609 3.2107 10.5858 3.5858C10.2107 3.9609 10 4.4696 10 5C10 5.5304 10.2107 6.0391 10.5858 6.4142C10.9609 6.7893 11.4696 7 12 7ZM12 14C12.5304 14 13.0391 13.7893 13.4142 13.4142C13.7893 13.0391 14 12.5304 14 12C14 11.4696 13.7893 10.9609 13.4142 10.5858C13.0391 10.2107 12.5304 10 12 10C11.4696 10 10.9609 10.2107 10.5858 10.5858C10.2107 10.9609 10 11.4696 10 12C10 12.5304 10.2107 13.0391 10.5858 13.4142C10.9609 13.7893 11.4696 14 12 14ZM12 21C12.5304 21 13.0391 20.7893 13.4142 20.4142C13.7893 20.0391 14 19.5304 14 19C14 18.4696 13.7893 17.9609 13.4142 17.5858C13.0391 17.2107 12.5304 17 12 17C11.4696 17 10.9609 17.2107 10.5858 17.5858C10.2107 17.9609 10 18.4696 10 19C10 19.5304 10.2107 20.0391 10.5858 20.4142C10.9609 20.7893 11.4696 21 12 21Z" />
  </svg>
);

export const TrendingIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 20" {...rest}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.80344 2C8.59013 2.0002 8.38212 2.06649 8.20804 2.18977C8.03882 2.32378 7.92411 2.51473 7.88527 2.72706C7.84642 2.93938 7.88611 3.15858 7.99692 3.34381C8.9517 4.99837 9.3265 7.0384 8.82598 8.61349C8.7216 8.9444 8.47727 9.09622 8.21515 9.11164C8.05723 9.1171 7.90089 9.07864 7.76352 9.00054C7.62616 8.92243 7.51316 8.80775 7.4371 8.66924C7.323 8.4752 7.22004 8.27483 7.12872 8.06909C7.0954 7.99373 7.04675 7.92613 6.98586 7.87061C6.92496 7.8151 6.85317 7.77288 6.77506 7.74666C6.69694 7.72043 6.61422 7.71078 6.53216 7.7183C6.45011 7.72583 6.37052 7.75037 6.29847 7.79037C5.49715 8.23536 4.83304 8.89139 4.37825 9.68721C3.92347 10.483 3.69539 11.3882 3.71879 12.3045V12.3128C3.78283 13.914 4.33554 15.3491 5.41486 16.3858C6.49773 17.4236 8.04792 18 9.99781 18C11.956 18 13.505 17.3892 14.5819 16.3395C15.6553 15.2922 16.2092 13.8606 16.278 12.3152C16.4369 8.6384 13.8561 4.24403 9.30277 2.12691C9.14954 2.04466 8.97735 2.00109 8.80344 2Z"
    />
  </svg>
);

export const WlbFeedIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 20" {...rest}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M2.5 8.33366C2.5 5.19116 2.5 3.61949 3.47667 2.64366C4.45333 1.66783 6.02417 1.66699 9.16667 1.66699H10.8333C13.9758 1.66699 15.5475 1.66699 16.5233 2.64366C17.4992 3.62033 17.5 5.19116 17.5 8.33366V11.667C17.5 14.8095 17.5 16.3812 16.5233 17.357C15.5467 18.3328 13.9758 18.3337 10.8333 18.3337H9.16667C6.02417 18.3337 4.4525 18.3337 3.47667 17.357C2.50083 16.3803 2.5 14.8095 2.5 11.667V8.33366ZM5 10.0003C5 8.82199 5 8.23283 5.36667 7.86699C5.73167 7.50033 6.32083 7.50033 7.5 7.50033H12.5C13.6783 7.50033 14.2675 7.50033 14.6333 7.86699C15 8.23283 15 8.82199 15 10.0003V13.3337C15 14.512 15 15.1012 14.6333 15.467C14.2675 15.8337 13.6783 15.8337 12.5 15.8337H7.5C6.32167 15.8337 5.7325 15.8337 5.36667 15.467C5 15.102 5 14.5128 5 13.3337V10.0003ZM5.83333 4.37533C5.66757 4.37533 5.5086 4.44117 5.39139 4.55838C5.27418 4.67559 5.20833 4.83457 5.20833 5.00033C5.20833 5.16609 5.27418 5.32506 5.39139 5.44227C5.5086 5.55948 5.66757 5.62533 5.83333 5.62533H10C10.1658 5.62533 10.3247 5.55948 10.4419 5.44227C10.5592 5.32506 10.625 5.16609 10.625 5.00033C10.625 4.83457 10.5592 4.67559 10.4419 4.55838C10.3247 4.44117 10.1658 4.37533 10 4.37533H5.83333Z"
    />
  </svg>
);

export const BuddiesTabIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 20" {...rest}>
    <path d="M7.5 5C6.83696 5 6.20107 5.26339 5.73223 5.73223C5.26339 6.20107 5 6.83696 5 7.5C5 8.16304 5.26339 8.79893 5.73223 9.26777C6.20107 9.73661 6.83696 10 7.5 10C8.16304 10 8.79893 9.73661 9.26777 9.26777C9.73661 8.79893 10 8.16304 10 7.5C10 6.83696 9.73661 6.20107 9.26777 5.73223C8.79893 5.26339 8.16304 5 7.5 5ZM10.625 11.25H4.375C3.87772 11.25 3.40081 11.4475 3.04917 11.7992C2.69754 12.1508 2.5 12.6277 2.5 13.125C2.5 14.52 3.07375 15.6375 4.015 16.3938C4.94125 17.1375 6.18375 17.5 7.5 17.5C8.81625 17.5 10.0587 17.1375 10.985 16.3938C11.925 15.6375 12.5 14.52 12.5 13.125C12.5 12.6277 12.3025 12.1508 11.9508 11.7992C11.5992 11.4475 11.1223 11.25 10.625 11.25Z" />
    <path d="M13.7498 8.33366L13.2665 7.85409C11.5498 6.15746 10.4165 5.03484 10.4165 3.66518C10.4165 2.54256 11.2232 1.66699 12.2498 1.66699C12.8298 1.66699 13.3865 1.96127 13.7498 2.42267C14.1132 1.96127 14.6698 1.66699 15.2498 1.66699C16.2765 1.66699 17.0832 2.54256 17.0832 3.66518C17.0832 5.03484 15.9498 6.15746 14.2332 7.85409L13.7498 8.33366Z" />
  </svg>
);

export const GroupsTabIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 18 18" {...rest}>
    <path d="M6.25 7.5C7.24456 7.5 8.19839 7.10491 8.90165 6.40165C9.60491 5.69839 10 4.74456 10 3.75C10 2.75544 9.60491 1.80161 8.90165 1.09835C8.19839 0.395088 7.24456 0 6.25 0C5.25544 0 4.30161 0.395088 3.59835 1.09835C2.89509 1.80161 2.5 2.75544 2.5 3.75C2.5 4.74456 2.89509 5.69839 3.59835 6.40165C4.30161 7.10491 5.25544 7.5 6.25 7.5ZM15.8333 5C15.8333 5.66304 15.5699 6.29893 15.1011 6.76777C14.6323 7.23661 13.9964 7.5 13.3333 7.5C12.6703 7.5 12.0344 7.23661 11.5656 6.76777C11.0967 6.29893 10.8333 5.66304 10.8333 5C10.8333 4.33696 11.0967 3.70107 11.5656 3.23223C12.0344 2.76339 12.6703 2.5 13.3333 2.5C13.9964 2.5 14.6323 2.76339 15.1011 3.23223C15.5699 3.70107 15.8333 4.33696 15.8333 5ZM6.25 8.33333C7.91083 8.33333 9.42083 8.84 10.5308 9.555C11.0867 9.91333 11.5625 10.3358 11.9083 10.7967C12.2475 11.2508 12.5 11.7967 12.5 12.3808C12.5 13.01 12.2083 13.5142 11.78 13.8783C11.3758 14.22 10.8492 14.4417 10.305 14.5942C9.21083 14.9 7.76333 15 6.25 15C4.73667 15 3.28917 14.9 2.19583 14.5942C1.65083 14.4417 1.12417 14.22 0.720833 13.8783C0.290833 13.515 0 13.01 0 12.3808C0 11.7958 0.2525 11.2508 0.591667 10.7975C0.9375 10.3358 1.41333 9.9125 1.96917 9.55583C3.07917 8.83917 4.58917 8.33333 6.25 8.33333ZM13.3333 8.33333C14.4333 8.33333 15.4317 8.69667 16.1667 9.20917C16.8517 9.68667 17.5 10.44 17.5 11.3092C17.5 11.7983 17.2892 12.2033 16.9683 12.4975C16.6708 12.7708 16.2942 12.9375 15.9317 13.0475C15.2067 13.2667 14.2733 13.3333 13.3333 13.3333H13.1642C13.2725 13.0433 13.3333 12.725 13.3333 12.3808C13.3333 11.5558 12.9808 10.8392 12.575 10.2975C12.17 9.75667 11.6333 9.28083 11.0342 8.88833C11.7458 8.52394 12.5338 8.33372 13.3333 8.33333Z" />
  </svg>
);

/** Two-person group silhouette used by the create-group conversation action. */
export const CreateGroupIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="8" cy="8" r="3.5" />
    <path d="M2 20c0-3.4 2.7-6 6-6 2.2 0 4.1 1.1 5.1 2.8" />
    <circle cx="16.5" cy="9" r="2.5" />
    <path d="M15 15c.5-.2 1-.3 1.5-.3 2.5 0 4.5 1.8 4.5 4" />
  </svg>
);

export const SettingsIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19 12a7 7 0 0 0-.15-1.4l2-1.5-2-3.4-2.3.9a7 7 0 0 0-2.4-1.4L13.7 3h-3.4l-.45 2.6a7 7 0 0 0-2.4 1.4l-2.3-.9-2 3.4 2 1.5a7 7 0 0 0 0 2.8l-2 1.5 2 3.4 2.3-.9a7 7 0 0 0 2.4 1.4l.45 2.6h3.4l.45-2.6a7 7 0 0 0 2.4-1.4l2.3.9 2-3.4-2-1.5c.1-.45.15-.9.15-1.4Z" />
  </svg>
);

export const InfoIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 11v5" />
    <path d="M12 8h.01" />
  </svg>
);

export const CloseIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M6 6l12 12M18 6 6 18" />
  </svg>
);

export const HeartIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 24 24" {...rest}>
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8Z" />
  </svg>
);

export const CommentIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 20 20" {...rest}>
    <path d="M17.4999 12.5C17.4999 12.942 17.3243 13.366 17.0118 13.6785C16.6992 13.9911 16.2753 14.1667 15.8333 14.1667H5.83325L2.49992 17.5V4.16667C2.49992 3.72464 2.67551 3.30072 2.98807 2.98816C3.30063 2.67559 3.72456 2.5 4.16659 2.5H15.8333C16.2753 2.5 16.6992 2.67559 17.0118 2.98816C17.3243 3.30072 17.4999 3.72464 17.4999 4.16667V12.5Z" strokeWidth="1.66667" />
  </svg>
);

export const ShareIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 24 24" {...rest}>
    <path d="m22 2-7 20-4-9-9-4Z" />
    <path d="M22 2 11 13" />
  </svg>
);

export const ReshareIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} viewBox="0 0 24 24" {...rest}>
    <path d="m17 1 4 4-4 4" />
    <path d="M3 11V9a4 4 0 0 1 4-4h14" />
    <path d="m7 23-4-4 4-4" />
    <path d="M21 13v2a4 4 0 0 1-4 4H3" />
  </svg>
);

export const LinkIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

export const LockIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="5" y="10" width="14" height="10" rx="2" />
    <path d="M8 10V7a4 4 0 0 1 8 0v3" />
  </svg>
);

export const BlogIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M6 4.5h10a2 2 0 0 1 2 2V20H8a2 2 0 0 1-2-2V4.5Z" />
    <path d="M8 4.5v13a2 2 0 0 0 2 2M10 8h5M10 11h5" />
  </svg>
);

export const BookmarkIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M6 4.5h12v16l-6-3-6 3v-16Z" />
  </svg>
);

export const ChartIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M4 19V5M4 19h17" />
    <path d="m7 15 3-4 3 2 5-6" />
  </svg>
);

export const AwardIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="9" r="4" />
    <path d="m9 12-1 8 4-2 4 2-1-8" />
  </svg>
);

export const LeaderboardIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M5 20V10h4v10M10 20V4h4v16M15 20v-7h4v7" />
  </svg>
);

export const HeadsetIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M4 14v-2a8 8 0 0 1 16 0v2" />
    <path d="M4 14h3v5H5a1 1 0 0 1-1-1v-4ZM20 14h-3v5h2a1 1 0 0 0 1-1v-4Z" />
  </svg>
);

export const LogoutIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M14 5H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h8" />
    <path d="m15 8 4 4-4 4M19 12H9" />
  </svg>
);

export const ChevronDownIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="m6 9 6 6 6-6" />
  </svg>
);

export const GlobeIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3.5 9h17M3.5 15h17" />
    <path d="M12 3c2.3 2.4 3.5 5.4 3.5 9s-1.2 6.6-3.5 9c-2.3-2.4-3.5-5.4-3.5-9s1.2-6.6 3.5-9Z" />
  </svg>
);

export const ChevronLeftIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="m15 6-6 6 6 6" />
  </svg>
);

export const ChevronUpIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="m6 15 6-6 6 6" />
  </svg>
);

export const ArrowRightIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M4 12h16m-6-6 6 6-6 6" />
  </svg>
);

export const PlusIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 5v14M5 12h14" />
  </svg>
);

export const PlusCircleIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <circle cx="12" cy="12" r="10" />
    <path d="M11 7h2v4h4v2h-4v4h-2v-4H7v-2h4z" fill="var(--color-surface)" />
  </svg>
);

export const SearchIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="10.5" cy="10.5" r="6.5" />
    <path d="m16 16 4 4" />
  </svg>
);

export const SmileIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.5c.9 1.1 2.1 1.7 3.5 1.7s2.6-.6 3.5-1.7" />
    <path d="M9 9.5h.01" />
    <path d="M15 9.5h.01" />
  </svg>
);

export const AlertCircleIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7.5v5" />
    <path d="M12 16.2h.01" />
  </svg>
);

/** Shield with a clock face — content saved but awaiting moderator review. */
export const ReviewShieldIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 3l7 3v5.5c0 4.3-2.9 7.6-7 9.5-4.1-1.9-7-5.2-7-9.5V6z" />
    <path d="M12 9v3l2 1.5" />
  </svg>
);

export const TrashIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M4 7h16" />
    <path d="M9 7V4h6v3" />
    <path d="m6.5 7 1 13h9l1-13" />
    <path d="M10 10.5v6M14 10.5v6" />
  </svg>
);

export const PostImageIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 20" {...rest}>
    <path d="M4.16667 17.5C3.70833 17.5 3.31611 17.3369 2.99 17.0108C2.66389 16.6847 2.50056 16.2922 2.5 15.8333V4.16667C2.5 3.70833 2.66333 3.31611 2.99 2.99C3.31667 2.66389 3.70889 2.50056 4.16667 2.5H15.8333C16.2917 2.5 16.6842 2.66333 17.0108 2.99C17.3375 3.31667 17.5006 3.70889 17.5 4.16667V15.8333C17.5 16.2917 17.3369 16.6842 17.0108 17.0108C16.6847 17.3375 16.2922 17.5006 15.8333 17.5H4.16667ZM4.16667 15.8333H15.8333V4.16667H4.16667V15.8333ZM5 14.1667H15L11.875 10L9.375 13.3333L7.5 10.8333L5 14.1667Z" />
  </svg>
);

export const PostVideoIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 20" {...rest}>
    <path d="M15.0013 4.99967C15.0013 4.08301 14.2513 3.33301 13.3346 3.33301H3.33464C2.41797 3.33301 1.66797 4.08301 1.66797 4.99967V14.9997C1.66797 15.9163 2.41797 16.6663 3.33464 16.6663H13.3346C14.2513 16.6663 15.0013 15.9163 15.0013 14.9997V11.3913L18.3346 14.1663V5.83301L15.0013 8.60801V4.99967ZM13.3346 14.9997H3.33464V4.99967H13.3346V14.9997Z" />
  </svg>
);

export const PostGifIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 20" {...rest}>
    <path d="M1.66667 14.1667V5.83333H8.33333V7.5H3.33333V12.5H6.66667V10.8333H5V9.16667H8.33333V14.1667H1.66667ZM10 14.1667V5.83333H11.6667V14.1667H10ZM13.3333 14.1667V5.83333H19.1667V7.5H15V9.16667H18.3333V10.8333H15V14.1667H13.3333Z" />
  </svg>
);

export const PostFeelingIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 20" {...rest}>
    <path
      transform="translate(1.6667 1.6667)"
      d="M8.33333 0C12.9358 0 16.6667 3.73083 16.6667 8.33333C16.6667 12.9358 12.9358 16.6667 8.33333 16.6667C3.73083 16.6667 0 12.9358 0 8.33333C0 3.73083 3.73083 0 8.33333 0ZM8.33333 1.66667C6.56522 1.66667 4.86953 2.36905 3.61929 3.61929C2.36905 4.86953 1.66667 6.56522 1.66667 8.33333C1.66667 10.1014 2.36905 11.7971 3.61929 13.0474C4.86953 14.2976 6.56522 15 8.33333 15C10.1014 15 11.7971 14.2976 13.0474 13.0474C14.2976 11.7971 15 10.1014 15 8.33333C15 6.56522 14.2976 4.86953 13.0474 3.61929C11.7971 2.36905 10.1014 1.66667 8.33333 1.66667ZM10.6667 9.88083C10.7444 9.8024 10.837 9.7402 10.939 9.69786C11.041 9.65551 11.1504 9.63387 11.2608 9.63417C11.3712 9.63447 11.4805 9.65673 11.5823 9.69964C11.684 9.74254 11.7762 9.80525 11.8536 9.88411C11.9309 9.96297 11.9917 10.0564 12.0326 10.159C12.0735 10.2616 12.0936 10.3713 12.0917 10.4817C12.0898 10.5921 12.066 10.7011 12.0217 10.8022C11.9773 10.9033 11.9133 10.9947 11.8333 11.0708C10.8997 11.9884 9.64243 12.5018 8.33333 12.5C7.02424 12.5018 5.767 11.9884 4.83333 11.0708C4.67919 10.9153 4.59244 10.7054 4.59184 10.4865C4.59123 10.2675 4.67682 10.0571 4.83011 9.90077C4.98339 9.74443 5.19204 9.65468 5.41096 9.65095C5.62988 9.64722 5.84148 9.7298 6 9.88083C6.62215 10.493 7.46052 10.8352 8.33333 10.8333C9.24167 10.8333 10.0642 10.4708 10.6667 9.88083ZM5.41667 5C5.74819 5 6.06613 5.1317 6.30055 5.36612C6.53497 5.60054 6.66667 5.91848 6.66667 6.25C6.66667 6.58152 6.53497 6.89946 6.30055 7.13388C6.06613 7.3683 5.74819 7.5 5.41667 7.5C5.08515 7.5 4.7672 7.3683 4.53278 7.13388C4.29836 6.89946 4.16667 6.58152 4.16667 6.25C4.16667 5.91848 4.29836 5.60054 4.53278 5.36612C4.7672 5.1317 5.08515 5 5.41667 5ZM11.25 5C11.5815 5 11.8995 5.1317 12.1339 5.36612C12.3683 5.60054 12.5 5.91848 12.5 6.25C12.5 6.58152 12.3683 6.89946 12.1339 7.13388C11.8995 7.3683 11.5815 7.5 11.25 7.5C10.9185 7.5 10.6005 7.3683 10.3661 7.13388C10.1317 6.89946 10 6.58152 10 6.25C10 5.91848 10.1317 5.60054 10.3661 5.36612C10.6005 5.1317 10.9185 5 11.25 5Z"
    />
  </svg>
);

export const CameraIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);

export const CameraFilledIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M9 3 7.17 5H4a2 2 0 0 0-2 2v11a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-3.17L15 3H9Zm3 14a5 5 0 1 1 0-10 5 5 0 0 1 0 10Zm0-2.2a2.8 2.8 0 1 0 0-5.6 2.8 2.8 0 0 0 0 5.6Z" />
  </svg>
);

export const MapPinIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
    <circle cx="12" cy="10" r="3" />
  </svg>
);

export const CalendarIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <path d="M16 2v4M8 2v4M3 10h18" />
  </svg>
);

export const TrophyIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M6 4h12v5a6 6 0 0 1-12 0z" />
    <path d="M6 6H4a2 2 0 0 0 0 4h2M18 6h2a2 2 0 0 1 0 4h-2" />
    <path d="M10 15h4M9 20h6M12 15v5" />
  </svg>
);

export const WeightScaleIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="3" y="3" width="18" height="18" rx="4" />
    <path d="M8.5 9.5A5 5 0 0 1 12 8a5 5 0 0 1 3.5 1.5" />
    <path d="M12 15l2.5-3.5" />
  </svg>
);

export const RulerIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="2" y="8" width="20" height="8" rx="2" />
    <path d="M6 8v3M10 8v4M14 8v3M18 8v4" />
  </svg>
);

export const FlameIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 2s5 4.5 5 9a5 5 0 0 1-10 0c0-1.6.6-3 1.5-4.2C9 8.5 9.5 9.5 10.5 10c0-2.6.6-5.6 1.5-8z" />
  </svg>
);

export const PencilIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

/** Matches the mobile post menu's Ionicons `create-outline` treatment. */
export const EditPostIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

export const EditFilledIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25Zm17.71-10.04a1 1 0 0 0 0-1.42l-2.5-2.5a1 1 0 0 0-1.42 0l-1.96 1.96 3.75 3.75 2.13-1.79Z" />
  </svg>
);

export const PostHashtagIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} viewBox="0 0 20 20" {...rest}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      transform="translate(1.792 1.792)"
      d="M6.98812 0.0102847C7.32458 -0.0203289 7.66375 0.0175723 7.98516 0.121699C8.30656 0.225827 8.60353 0.394019 8.85812 0.616118L8.98312 0.732785L15.5015 7.25195C15.95 7.70064 16.2113 8.3031 16.2322 8.93723C16.253 9.57136 16.032 10.1897 15.614 10.667L15.5015 10.787L10.7873 15.5011C10.3386 15.9497 9.73613 16.2109 9.102 16.2318C8.46787 16.2527 7.84952 16.0317 7.37228 15.6136L7.25228 15.5011L0.732283 8.98195C0.493513 8.7432 0.305584 8.45854 0.179834 8.14517C0.0540833 7.8318 -0.00688177 7.4962 0.000616532 7.15862L0.00978333 6.98779L0.403117 2.66695C0.453615 2.1101 0.689326 1.58626 1.07258 1.17914C1.45583 0.772018 1.9645 0.505126 2.51728 0.421118L2.66645 0.403618L6.98812 0.0102847ZM7.24478 1.66778L7.13895 1.67028L2.81812 2.06362C2.64092 2.07962 2.47349 2.15191 2.34033 2.26992C2.20718 2.38793 2.11529 2.54546 2.07812 2.71945L2.06395 2.81778L1.67062 7.13862C1.65168 7.34858 1.71302 7.55792 1.84228 7.72445L1.91145 7.80362L8.43062 14.3228C8.57411 14.4663 8.76503 14.5525 8.96755 14.5652C9.17007 14.5779 9.37028 14.5163 9.53062 14.392L9.60895 14.3228L14.3231 9.60862C14.4666 9.46512 14.5528 9.27421 14.5655 9.07169C14.5783 8.86916 14.5167 8.66896 14.3923 8.50862L14.3231 8.43029L7.80395 1.91112C7.655 1.76227 7.45522 1.67533 7.24478 1.66778ZM4.30478 4.30528C4.53695 4.07312 4.81257 3.88895 5.11591 3.76331C5.41925 3.63766 5.74437 3.57299 6.0727 3.57299C6.40103 3.57299 6.72615 3.63766 7.02949 3.76331C7.33283 3.88895 7.60845 4.07312 7.84062 4.30528C8.07278 4.53745 8.25695 4.81307 8.38259 5.11641C8.50824 5.41975 8.57291 5.74487 8.57291 6.0732C8.57291 6.40153 8.50824 6.72665 8.38259 7.02999C8.25695 7.33333 8.07278 7.60895 7.84062 7.84112C7.37174 8.31 6.7358 8.57341 6.0727 8.57341C5.4096 8.57341 4.77366 8.31 4.30478 7.84112C3.8359 7.37224 3.57249 6.7363 3.57249 6.0732C3.57249 5.4101 3.8359 4.77417 4.30478 4.30528ZM6.66228 5.48362C6.58491 5.40619 6.49305 5.34477 6.39194 5.30284C6.29083 5.26092 6.18245 5.23932 6.07299 5.23928C5.96354 5.23924 5.85514 5.26076 5.754 5.30262C5.65287 5.34447 5.56096 5.40583 5.48353 5.4832C5.40611 5.56057 5.34468 5.65243 5.30276 5.75354C5.26083 5.85466 5.23924 5.96303 5.2392 6.07249C5.23916 6.18195 5.26068 6.29034 5.30253 6.39148C5.34438 6.49262 5.40575 6.58453 5.48312 6.66195C5.63937 6.81832 5.85135 6.90621 6.07241 6.90629C6.29346 6.90637 6.5055 6.81862 6.66187 6.66237C6.81823 6.50611 6.90612 6.29414 6.9062 6.07308C6.90628 5.85202 6.81854 5.63999 6.66228 5.48362Z"
    />
  </svg>
);

export const SendIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 19V5" />
    <path d="m5 12 7-7 7 7" />
  </svg>
);

export const PhoneIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92Z" />
  </svg>
);

export const VideoCallIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="m22 8-6 4 6 4V8Z" />
    <rect width="14" height="12" x="2" y="6" rx="2" ry="2" />
  </svg>
);

export const ReplyIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <polyline points="9 17 4 12 9 7" />
    <path d="M20 18v-2a4 4 0 0 0-4-4H4" />
  </svg>
);

export const PinIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 17v5" />
    <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 1 1-1 2 2 0 0 0 0-4H8a2 2 0 0 0 0 4 1 1 0 0 1 1 1z" />
  </svg>
);

export const BellOffIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M8.7 3A6 6 0 0 1 18 8a21.3 21.3 0 0 0 .6 5" />
    <path d="M17 17H3s3-2 3-9a4.7 4.7 0 0 1 .3-2" />
    <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
    <path d="m2 2 20 20" />
  </svg>
);

export const StarIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export const BlockIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="9" cy="7" r="4" />
    <path d="M2 21v-2a7 7 0 0 1 11.5-5.4" />
    <path d="m17 8 5 5M22 8l-5 5" />
  </svg>
);
export const MailIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="2" y="4" width="20" height="16" rx="2" />
    <path d="m2 7 10 6 10-6" />
  </svg>
);
export const ShieldIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 3 4 6v6c0 5 3.4 8.2 8 9 4.6-.8 8-4 8-9V6z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);
export const DocumentIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M14 2H7a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7z" />
    <path d="M14 2v5h5" />
    <path d="M9 13h6" />
    <path d="M9 17h6" />
  </svg>
);
export const GridIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);
export const TableIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <path d="M3 9h18M9 9v11" />
  </svg>
);
export const WarningTriangleIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M10.3 4.2 2.6 18a2 2 0 0 0 1.7 3h15.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
    <path d="M12 9v4M12 17h.01" />
  </svg>
);
export const MedicalCrossIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="4" y="4" width="16" height="16" rx="4" />
    <path d="M12 8v8M8 12h8" />
  </svg>
);
export const StethoscopeIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M6 3v5a6 6 0 0 0 12 0V3M4 3h4M16 3h4" />
    <path d="M12 14v2a4 4 0 0 0 8 0v-2" />
    <circle cx="20" cy="12" r="2" />
  </svg>
);
export const HeartPulseIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.7l-1.1-1.1a5.5 5.5 0 0 0-7.8 7.8L12 21l8.8-8.6a5.5 5.5 0 0 0 0-7.8Z" />
    <path d="M3.5 12h4l1.5-3 3 6 1.5-3h7" />
  </svg>
);
export const UsersIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="9" cy="8" r="3" />
    <path d="M3 20c0-3.3 2.7-6 6-6s6 2.7 6 6" />
    <circle cx="17" cy="9" r="2.5" />
    <path d="M15 15c.6-.3 1.3-.5 2-.5 2.5 0 4.5 2 4.5 4.5" />
  </svg>
);
export const ShieldAlertIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M12 3 4 6v6c0 5 3.4 8.2 8 9 4.6-.8 8-4 8-9V6z" />
    <path d="M12 8v5M12 16h.01" />
  </svg>
);
export const MegaphoneIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="m3 11 15-6v14L3 13zM18 10a3 3 0 0 1 0 4M6 14l1.5 6h4L10 15" />
  </svg>
);
export const DiscussionIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M14 4H5a3 3 0 0 0-3 3v9l3-3h9a3 3 0 0 0 3-3V7a3 3 0 0 0-3-3Z" />
    <path d="M17 9h2a3 3 0 0 1 3 3v8l-3-3h-5a3 3 0 0 1-3-3v-1" />
  </svg>
);
export const FlagIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M5 22V4M5 4c5-4 9 4 14 0v10c-5 4-9-4-14 0" />
  </svg>
);
export const HandIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M7 11V6a2 2 0 0 1 4 0v4-6a2 2 0 0 1 4 0v6-4a2 2 0 0 1 4 0v7c0 5-3 8-8 8h-1c-3 0-5-2-7-5l-1-2a2 2 0 0 1 3-2l2 2" />
  </svg>
);
export const TagIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M20.6 13.6 11 4H4v7l9.6 9.6a2 2 0 0 0 2.8 0l4.2-4.2a2 2 0 0 0 0-2.8Z" />
    <circle cx="7.5" cy="7.5" r="1" />
  </svg>
);
export const LayersIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="m12 2 9 5-9 5-9-5 9-5Z" />
    <path d="m3 12 9 5 9-5M3 17l9 5 9-5" />
  </svg>
);
export const ClockIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </svg>
);
export const CheckCircleIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="9" />
    <path d="m8.5 12.2 2.4 2.4 4.6-4.9" />
  </svg>
);
export const ChevronRightIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="m9 18 6-6-6-6" />
  </svg>
);
export const SunIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M6.34 17.66l-1.41 1.41M19.07 4.93l-1.41 1.41" />
  </svg>
);
export const MoonIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
  </svg>
);
export const MonitorIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="2" y="3" width="20" height="14" rx="2" />
    <path d="M8 21h8M12 17v4" />
  </svg>
);
export const CheckIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="m5 12 5 5 9-11" />
  </svg>
);
export const EyeIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7-10-7-10-7z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
export const EyeFilledIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M12 4.5C6.5 4.5 2.2 8 1 12c1.2 4 5.5 7.5 11 7.5S21.8 16 23 12c-1.2-4-5.5-7.5-11-7.5Zm0 12.25A4.75 4.75 0 1 1 12 7.25a4.75 4.75 0 0 1 0 9.5Zm0-2.25a2.5 2.5 0 1 0 0-5 2.5 2.5 0 0 0 0 5Z" />
  </svg>
);
export const EyeOffIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M10.6 6.14A9.7 9.7 0 0 1 12 6c6.4 0 10 7 10 7a17.4 17.4 0 0 1-3.2 4.02M6.5 7.4A17.3 17.3 0 0 0 2 13s3.6 7 10 7a9.6 9.6 0 0 0 4.2-.94" />
    <path d="M9.9 10.1a3 3 0 0 0 4.2 4.2" />
    <path d="m3 3 18 18" />
  </svg>
);

/* Brand marks. Google is omitted on purpose: Google Identity Services renders
   its own button, logo included. */

export const FacebookIcon = ({ size = 20, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.313 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073Z" />
  </svg>
);

export const InstagramIcon = ({ size = 20, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <rect x="3" y="3" width="18" height="18" rx="5" />
    <circle cx="12" cy="12" r="4" />
    <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
  </svg>
);

export const XIcon = ({ size = 20, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.657l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25h6.826l4.713 6.231 5.451-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77Z" />
  </svg>
);

export const LinkedInIcon = ({ size = 20, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.047c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286h-.002ZM5.337 7.433a2.062 2.062 0 1 1 0-4.124 2.062 2.062 0 0 1 0 4.124ZM7.119 20.452H3.555V9h3.564v11.452ZM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003Z" />
  </svg>
);

export const AppleIcon = ({ size = 20, ...rest }: IconProps) => (
  <svg {...filled(size)} {...rest}>
    <path d="M17.05 12.536c-.026-2.9 2.368-4.29 2.476-4.357-1.348-1.97-3.446-2.24-4.194-2.27-1.786-.18-3.487 1.05-4.394 1.05-.906 0-2.303-1.025-3.786-.997-1.948.029-3.744 1.132-4.746 2.876-2.023 3.508-.517 8.706 1.453 11.554.963 1.394 2.11 2.958 3.617 2.902 1.452-.058 2.001-.94 3.756-.94 1.756 0 2.25.94 3.787.91 1.563-.026 2.552-1.42 3.507-2.82 1.105-1.617 1.56-3.18 1.586-3.26-.035-.014-3.043-1.167-3.073-4.63M14.27 3.9c.8-.97 1.34-2.318 1.192-3.66-1.152.047-2.548.767-3.375 1.735-.742.86-1.39 2.232-1.216 3.548 1.286.1 2.598-.653 3.399-1.622" />
  </svg>
);

export const TrendingDownIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M22 17H2l8-8 4 4 6-6" />
    <path d="M16 7h6v6" />
  </svg>
);

export const TargetIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

export const RefreshIcon = ({ size = 24, ...rest }: IconProps) => (
  <svg {...base(size)} {...rest}>
    <path d="M20.5 12a8.5 8.5 0 1 1-2.49-6.01" />
    <path d="M20.5 4v5h-5" />
  </svg>
);
